import { Pathname } from "../../constants/pathnames/pathname";
import { PathnameAccounting } from "../../constants/pathnames/pathname.accounting";
import { PathnameInventory } from "../../constants/pathnames/pathname.inventory";
import { PathnamePurchase } from "../../constants/pathnames/pathname.procurement";
import { PathnameCrm } from "../../constants/pathnames/pathname.crm";
import { PathnameRental } from "../../constants/pathnames/pathname.rental";
import {
  workOrderCrudPaths,
  billOfMaterialCrudPaths,
  buildOrderCrudPaths,
  jobCardsCrudPaths,
  productionPlanPaths,
  equipmentsCrudPaths,
  gateEntriesCrudPaths,
} from "../../constants/pathnames/pathname.manufacturing";
import { generateRouteWithId } from "../../utils/route-utils";

// ─── App-switcher ────────────────────────────────────────────────────────────

export const MODULE_META: Record<string, { meta: string; isNew?: boolean; fg: string }> = {
  "/dashboard/user":          { meta: "Users & Permissions",    fg: "#7C3AED" },
  "/dashboard/drive":         { meta: "Files & Documents",      fg: "#C2410C" },
  "/dashboard/accounting":    { meta: "Finance & Ledger",       fg: "#16A34A" },
  "/dashboard/inventory":     { meta: "Stock & Warehouses",     fg: "#4F46E5" },
  "/dashboard/project":       { meta: "Tasks & Milestones",     fg: "#BE185D" },
  "/dashboard/procurement":   { meta: "Orders & Vendors",       fg: "#BE123C" },
  "/dashboard/manufacturing": { meta: "Production & BOM",       fg: "#DC2626" },
  "/dashboard/crm":           { meta: "Leads & Pipelines",      fg: "#6D28D9" },
  "/dashboard/hrms":          { meta: "Employees & Payroll",    fg: "#0284C7", isNew: true },
  "/dashboard/office":        { meta: "Assets & Spaces",        fg: "#0D9488" },
  "/dashboard/rental":        { meta: "Rentals & Contracts",    fg: "#15803D", isNew: true },
  "/dashboard/settings":      { meta: "System Configuration",   fg: "#4338CA" },
  "/dashboard/onboarding":    { meta: "Setup & Guides",         fg: "#7C3AED" },
};

// ─── Global search ───────────────────────────────────────────────────────────

export const DOC_TYPE_LABEL: Record<string, string> = {
  // Account
  sales_invoices:          'Sales Invoices',
  purchase_invoices:       'Purchase Invoices',
  payment_entries:         'Payments',
  credit_notes:            'Credit Notes',
  debit_notes:             'Debit Notes',
  journal_entries:         'Journal Entries',
  expense_reimbursements:  'Expenses',
  cash_expenses:           'Cash Expenses',
  budgets:                 'Budgets',
  assets:                  'Fixed Assets',
  asset_transfers:         'Asset Transfers',
  parties:                 'Parties',
  // Sales / CRM
  sales_orders:            'Sales Orders',
  sl_sales_orders:         'Sales Orders',
  sl_quotation:            'Quotations',
  sl_delivery_orders:      'Delivery Orders',
  sl_customer_returns:     'Customer Returns',
  sl_opportunity_parties:  'Opportunities',
  sl_promotions:           'Promotions',
  // Purchase
  purchase_orders:         'Purchase Orders',
  pr_purchase_orders:      'Purchase Orders',
  pr_purchase_requests:    'Purchase Requests',
  pr_rfqs:                 'RFQs',
  pr_vras:                 'Vendor Returns',
  pr_delivery_orders:      'Purchase Delivery Orders',
  pr_purchase_agreements:  'Purchase Agreements',
  // Inventory
  items:                   'Items',
  warehouse_location:      'Warehouses',
  inventory_adjustments:   'Stock Adjustments',
  bin_transfers:           'Bin Transfers',
  stock_transfers:         'Stock Transfers',
  // Manufacturing
  mf_work_orders:          'Work Orders',
  mf_bom:                  'Bill of Materials',
  mf_build_orders:         'Build Orders',
  mf_job_cards:            'Job Cards',
  mf_production_plan:      'Production Plans',
  mf_equipments:           'Equipment',
  mf_gate_registers:       'Gate Registers',
};

export const MODULE_THUMB_COLOR: Record<string, string> = {
  inventory:     'linear-gradient(135deg,#4F46E5,#3730A3)',
  account:       'linear-gradient(135deg,#16A34A,#15803D)',
  hrms:          'linear-gradient(135deg,#0284C7,#0369A1)',
  sales:         'linear-gradient(135deg,#C2410C,#9A3412)',
  purchase:      'linear-gradient(135deg,#BE123C,#9F1239)',
  manufacturing: 'linear-gradient(135deg,#DC2626,#B91C1C)',
  rental:        'linear-gradient(135deg,#15803D,#166534)',
  document:      'linear-gradient(135deg,#C2410C,#9A3412)',
};

const PathnameGeneratorAccounting    = (name: string) => Pathname.DASHBOARD_ACCOUNTING + name;
const PathnameGeneratorInventory     = (name: string) => Pathname.DASHBOARD_INVENTORY + name;
const PathnameGeneratorPurchase      = (name: string) => Pathname.DASHBOARD_PROCUREMENT + name;
const PathnameGeneratorCrm           = (name: string) => Pathname.DASHBOARD_CRM + name;
const PathnameGeneratorRental        = (name: string) => Pathname.DASHBOARD_RENTAL + name;
const PathnameGeneratorManufacturing = (name: string) => Pathname.DASHBOARD_MANUFACTURING + name;

export const getSearchResultUrl = (docType: string, sourceId: number, item?: any): string | null => {
  const id = sourceId.toString();
  const routeMap: Record<string, () => string> = {
    // ── Account ──────────────────────────────────────────────────────────────
    journal_entries:        () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_JOURNAL_ENTRY), id),
    payment_entries:        () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_PAYMENT_ENTRY), id),
    sales_invoices:         () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_SALES_INVOICES), id),
    purchase_invoices:      () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_PURCHASE_INVOICE), id),
    cash_expenses:          () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_CASH_EXPENSE), id),
    expense_reimbursements: () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_EXPENSE_REIMBURSEMENT), id),
    credit_notes:           () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_CREDIT_NOTE), id),
    debit_notes:            () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_DEBIT_NOTE), id),
    budgets:                () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_BUDGET), id),
    assets:                 () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_ASSET), id),
    asset_transfers:        () => generateRouteWithId(PathnameGeneratorAccounting(PathnameAccounting.VIEW_ASSET_TRANSFER), id),
    parties:                () => {
      const isVendor = item?.transaction_type === 'vendor';
      const route = isVendor ? PathnameAccounting.VIEW_VENDOR_MANAGEMENT : PathnameAccounting.VIEW_CUSTOMER_MANAGEMENT;
      return generateRouteWithId(PathnameGeneratorAccounting(route), id);
    },

    // ── Sales / CRM (both with and without sl_ prefix) ───────────────────────
    sales_orders:           () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_SALES_ORDER), id),
    sl_sales_orders:        () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_SALES_ORDER), id),
    sl_quotation:           () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_QUOTATION), id),
    sl_delivery_orders:     () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_DELIVERTY_ORDER), id),
    sl_customer_returns:    () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_CUSTOMER_RETURNS), id),
    sl_opportunity_parties: () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_OPPORTUNITY), id),
    sl_promotions:          () => generateRouteWithId(PathnameGeneratorCrm(PathnameCrm.VIEW_PROMOTIONS), id),

    // ── Purchase (both with and without pr_ prefix) ───────────────────────────
    purchase_orders:        () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER), id),
    pr_purchase_orders:     () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_ORDER), id),
    pr_purchase_requests:   () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_REQUEST), id),
    pr_rfqs:                () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_RFQ), id),
    pr_vras:                () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_VENDOR_RETURNS), id),
    pr_delivery_orders:     () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_DELIVERY_ORDER), id),
    pr_purchase_agreements: () => generateRouteWithId(PathnameGeneratorPurchase(PathnamePurchase.VIEW_PURCHASE_AGREEMENT), id),

    // ── Inventory ─────────────────────────────────────────────────────────────
    stock_transfers:        () => generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_STOCK_TRANSFER), id),
    items:                  () => generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_INVENTORY_ITEM), id),
    warehouse_location:     () => generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_LOCATION), id),
    inventory_adjustments:  () => generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_INVENTORY_ADJUSTMENT), id),
    bin_transfers:          () => generateRouteWithId(PathnameGeneratorInventory(PathnameInventory.VIEW_BIN_TRANSFER), id),

    // ── Manufacturing ─────────────────────────────────────────────────────────
    mf_work_orders:         () => generateRouteWithId(PathnameGeneratorManufacturing(workOrderCrudPaths.VIEW_WORK_ORDER), id),
    mf_bom:                 () => generateRouteWithId(PathnameGeneratorManufacturing(billOfMaterialCrudPaths.VIEW_BILL_OF_MATERIAL), id),
    mf_build_orders:        () => generateRouteWithId(PathnameGeneratorManufacturing(buildOrderCrudPaths.VIEW_BUILD_ORDER), id),
    mf_job_cards:           () => generateRouteWithId(PathnameGeneratorManufacturing(jobCardsCrudPaths.VIEW_JOB_CARDS), id),
    mf_production_plan:     () => generateRouteWithId(PathnameGeneratorManufacturing(productionPlanPaths.PRODUCTION_PLAN_VIEW), id),
    mf_equipments:          () => generateRouteWithId(PathnameGeneratorManufacturing(equipmentsCrudPaths.VIEW_EQUIPMENTS), id),
    mf_gate_registers:      () => generateRouteWithId(PathnameGeneratorManufacturing(gateEntriesCrudPaths.VIEW_GATE_REGISTER), id),

    // ── Rental ────────────────────────────────────────────────────────────────
    rental_orders:          () => generateRouteWithId(PathnameGeneratorRental(PathnameRental.VIEW_RENTAL_ORDER), id),
  };
  return routeMap[docType]?.() ?? null;
};
